import { DateTime } from "luxon"

import { iterateStringEnum } from "../../../../../lib/enums"
import { parseDateTimeFromApi } from "../../../../../lib/time"

import { IdCardHolderKind, IdCardStatus, IdCardStatusFilterOption, IdCardType } from "./idCardsListTypes"
import type {
    IdCardCardholderOption,
    IdCardCompanyOption,
    IdCardHolder,
    IdCardListItem,
    IdCardRow,
    IdCardsListFilters,
    IdCardsListView,
    IdCardStatusFilter,
} from "./idCardsListTypes"

export const ID_CARD_STATUS_L10N_BASE = "merchant-id-cards-list-status"
export const ID_CARD_TYPE_L10N_BASE = "merchant-id-cards-list-card-type"

export const ID_CARD_TYPES: ReadonlyArray<IdCardType> = [...iterateStringEnum(IdCardType)]

export const ID_CARD_STATUS_FILTERS: ReadonlyArray<IdCardStatusFilter> = [
    IdCardStatusFilterOption.ALL,
    IdCardStatusFilterOption.GUEST,
    IdCardStatus.ASSIGNED,
    IdCardStatus.UNASSIGNED,
    IdCardStatus.EXPIRED,
    IdCardStatus.INACTIVE,
]

export const DEFAULT_ID_CARDS_LIST_FILTERS: IdCardsListFilters = {
    status: IdCardStatusFilterOption.ALL,
}

const GUEST_CARDS_CARDHOLDER = "guest-cards"

export function formatCardNumber(cardNumber: string) {
    return cardNumber.replace(/(.{4})(?=.)/g, "$1 ")
}

export function getIdCardHolderLabel(holder: IdCardHolder) {
    return holder.kind === IdCardHolderKind.USER ? `${holder.name} — ${holder.employeeId}` : holder.name
}

function getIdCardStatus(card: IdCardListItem, today: DateTime) {
    if (card.isBlocked) return IdCardStatus.INACTIVE
    if (!card.holder) return IdCardStatus.UNASSIGNED
    if (card.validTo && parseDateTimeFromApi(card.validTo) < today) return IdCardStatus.EXPIRED
    return IdCardStatus.ASSIGNED
}

function isGuestCard(row: IdCardRow) {
    return row.holder?.kind === IdCardHolderKind.COMPANY
}

function matchesStatus(row: IdCardRow, status: IdCardStatusFilter) {
    if (status === IdCardStatusFilterOption.ALL) return true
    if (status === IdCardStatusFilterOption.GUEST) return isGuestCard(row)
    return row.status === status
}

function matchesCardholder(row: IdCardRow, cardholder: string | undefined) {
    if (!cardholder) return true
    if (cardholder === GUEST_CARDS_CARDHOLDER) return isGuestCard(row)
    return row.holder?.kind === IdCardHolderKind.USER && row.holder.employeeId === cardholder
}

function matchesSearch(row: IdCardRow, search: string | undefined) {
    const query = search?.trim().toLowerCase()
    if (!query) return true

    const texts = [row.holder && getIdCardHolderLabel(row.holder), row.company?.name, row.batch]

    return (
        row.cardNumber.toLowerCase().includes(query.replace(/\s+/g, "")) ||
        texts.some((text) => text?.toLowerCase().includes(query))
    )
}

function toCompanyOptions(rows: ReadonlyArray<IdCardRow>) {
    const options = new Map<string, IdCardCompanyOption>()

    rows.forEach(({ company }) => {
        if (!company) return
        options.set(company.id, { ...company, count: (options.get(company.id)?.count ?? 0) + 1 })
    })

    return [...options.values()].sort((a, b) => a.name.localeCompare(b.name))
}

function toCardholderOptions(rows: ReadonlyArray<IdCardRow>, guestCardsLabel: string) {
    const options = new Map<string, IdCardCardholderOption>()

    rows.forEach(({ holder, company }) => {
        if (holder?.kind !== IdCardHolderKind.USER) return
        options.set(holder.employeeId, {
            value: holder.employeeId,
            name: getIdCardHolderLabel(holder),
            companyName: company?.name,
            count: (options.get(holder.employeeId)?.count ?? 0) + 1,
        })
    })

    const guestOption: IdCardCardholderOption = {
        value: GUEST_CARDS_CARDHOLDER,
        name: guestCardsLabel,
        count: rows.filter(isGuestCard).length,
    }

    return [guestOption, ...[...options.values()].sort((a, b) => a.name.localeCompare(b.name))]
}

type IdCardsListViewParams = Readonly<{
    cards: ReadonlyArray<IdCardListItem>
    filters: IdCardsListFilters
    search: string | undefined
    guestCardsLabel: string
}>

export function toIdCardsListView({ cards, filters, search, guestCardsLabel }: IdCardsListViewParams): IdCardsListView {
    const today = DateTime.now().startOf("day")

    const allRows = cards
        .map((card): IdCardRow => ({ ...card, status: getIdCardStatus(card, today) }))
        .sort((a, b) => parseDateTimeFromApi(b.created).toMillis() - parseDateTimeFromApi(a.created).toMillis())

    const companyOptions = toCompanyOptions(allRows)
    // a selection outside the current period is ignored, so no invisible filter stays active
    const selectedCompany = companyOptions.find((option) => option.id === filters.companyId) ?? null
    const companyRows = selectedCompany ? allRows.filter((row) => row.company?.id === selectedCompany.id) : allRows

    const cardholderOptions = toCardholderOptions(companyRows, guestCardsLabel)
    const selectedCardholder = cardholderOptions.find((option) => option.value === filters.cardholder) ?? null

    const rows = companyRows.filter(
        (row) =>
            matchesStatus(row, filters.status) &&
            matchesCardholder(row, selectedCardholder?.value) &&
            (!filters.cardType || row.cardType === filters.cardType) &&
            matchesSearch(row, search),
    )

    const assignedCount = allRows.filter((row) => row.holder).length

    return {
        rows,
        total: allRows.length,
        assignedCount,
        unassignedCount: allRows.length - assignedCount,
        isFiltering:
            filters.status !== IdCardStatusFilterOption.ALL ||
            !!selectedCompany ||
            !!selectedCardholder ||
            !!filters.cardType ||
            !!search?.trim(),
        statusOptions: ID_CARD_STATUS_FILTERS.map((value) => ({
            value,
            count: allRows.filter((row) => matchesStatus(row, value)).length,
        })),
        companyOptions,
        cardholderOptions,
        selectedCompany,
        selectedCardholder,
    }
}
